// =============================================================================
// AssessmentFrameworkEditor.tsx
// =============================================================================
// Fase 2.6 Blokk D — vurderingsramme-seksjon på studieplan-form
// Grovkornet ramme som prøvesett + LMS-mapping bekreftes mot.
// Se docs/research/assessment-methods/14-prejudisielt-assessment-framework.md
// =============================================================================

import { useEffect } from 'react';
import { AlertCircle, Info } from 'lucide-react';

export interface AssessmentFramework {
  primary_method_class: 'written' | 'practical' | 'project' | 'oral' | 'observation' | 'mixed';
  flexibility: 'strict' | 'equivalent_allowed' | 'free';
  controlled_conditions_required: boolean;
  assessor_type_required: 'external_sensor' | 'internal_assessor' | 'any';
  bloom_min_level: number;
  rubric_required: boolean;
  notes?: string;
}

interface Props {
  value: AssessmentFramework | null;
  onChange: (framework: AssessmentFramework | null) => void;
  disabled?: boolean;
}

const CLASS_LABELS: Record<AssessmentFramework['primary_method_class'], string> = {
  written: 'Skriftlig prøve',
  practical: 'Praktisk prøve',
  project: 'Prosjekt / portefølje',
  oral: 'Muntlig prøve',
  observation: 'Observasjon / sensur under praksis',
  mixed: 'Kombinert (flere typer)',
};

const FLEX_LABELS: Record<AssessmentFramework['flexibility'], string> = {
  strict: 'Streng — prøve/LMS må matche eksakt',
  equivalent_allowed: 'Fleksibel — avvik tillatt med begrunnelse (anbefalt)',
  free: 'Fri — ingen håndhevelse (kun registrert)',
};

const ASSESSOR_LABELS: Record<AssessmentFramework['assessor_type_required'], string> = {
  external_sensor: 'Ekstern sensor',
  internal_assessor: 'Intern vurderer',
  any: 'Ingen krav',
};

// Default-anbefalinger per klasse (fra research-doc §4)
function defaultsForClass(klass: AssessmentFramework['primary_method_class']): Partial<AssessmentFramework> {
  switch (klass) {
    case 'practical':
    case 'project':
    case 'oral':
    case 'observation':
      return { rubric_required: true, controlled_conditions_required: false };
    case 'written':
      return { rubric_required: false, controlled_conditions_required: true };
    case 'mixed':
      return { rubric_required: false, controlled_conditions_required: false };
    default:
      return {};
  }
}

function emptyFramework(): AssessmentFramework {
  return {
    primary_method_class: 'written',
    flexibility: 'equivalent_allowed',
    controlled_conditions_required: true,
    assessor_type_required: 'external_sensor',
    bloom_min_level: 3,
    rubric_required: false,
    notes: '',
  };
}

export default function AssessmentFrameworkEditor({ value, onChange, disabled }: Props) {
  const enabled = value !== null;
  const framework = value || emptyFramework();

  const update = (patch: Partial<AssessmentFramework>) => {
    onChange({ ...framework, ...patch });
  };

  // Når klasse endres: anvend default-anbefalinger (bruker kan overstyre)
  const handleClassChange = (klass: AssessmentFramework['primary_method_class']) => {
    const defaults = defaultsForClass(klass);
    onChange({ ...framework, primary_method_class: klass, ...defaults });
  };

  useEffect(() => {
    // Ingen auto-aktivering ved mount — bruker velger eksplisitt
  }, []);

  return (
    <div className="card">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-semibold">Vurderingsramme (valgfritt)</h2>
        <label className="flex items-center gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            checked={enabled}
            disabled={disabled}
            onChange={(e) => onChange(e.target.checked ? emptyFramework() : null)}
          />
          Aktiver ramme
        </label>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-md p-3 mb-4 flex gap-2">
        <Info size={16} className="text-blue-600 mt-0.5 flex-shrink-0" />
        <div className="text-sm text-blue-900">
          <p className="font-medium mb-1">Hva er en vurderingsramme?</p>
          <p>
            Grovkornet beskrivelse av <em>hvordan</em> kompetansen skal prøves — ikke <em>hva</em>.
            Prøvesett og LMS-koblinger bekreftes mot denne rammen ved opprettelse.
            Rammen er valgfri; uten den fungerer systemet som før (ingen håndhevelse).
          </p>
        </div>
      </div>

      {!enabled && (
        <p className="text-sm text-gray-500 italic">
          Ingen vurderingsramme satt. Prøvesett og LMS-koblinger vil ikke bli bekreftet mot studieplanen.
        </p>
      )}

      {enabled && (
        <div className="space-y-4">
          {/* primary_method_class */}
          <div>
            <label className="label">Primær vurderingsklasse *</label>
            <select
              className="input"
              value={framework.primary_method_class}
              onChange={(e) => handleClassChange(e.target.value as AssessmentFramework['primary_method_class'])}
              disabled={disabled}
            >
              {Object.entries(CLASS_LABELS).map(([key, label]) => (
                <option key={key} value={key}>{label}</option>
              ))}
            </select>
          </div>

          {/* flexibility */}
          <div>
            <label className="label">Fleksibilitet *</label>
            <div className="space-y-2">
              {(Object.keys(FLEX_LABELS) as Array<AssessmentFramework['flexibility']>).map((key) => (
                <label key={key} className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="flexibility"
                    value={key}
                    checked={framework.flexibility === key}
                    onChange={() => update({ flexibility: key })}
                    disabled={disabled}
                    className="mt-1"
                  />
                  <span className="text-sm">{FLEX_LABELS[key]}</span>
                </label>
              ))}
            </div>
            {framework.flexibility === 'strict' && (
              <div className="mt-2 bg-amber-50 border border-amber-200 rounded-md p-2 flex gap-2">
                <AlertCircle size={14} className="text-amber-600 mt-0.5 flex-shrink-0" />
                <span className="text-xs text-amber-900">
                  Streng modus blokkerer prøvesett/LMS-koblinger som ikke matcher eksakt.
                  Bruk kun når det er krav om standardisering (f.eks. fag/svenneprøver).
                </span>
              </div>
            )}
            {framework.flexibility === 'free' && (
              <div className="mt-2 bg-gray-50 border border-gray-200 rounded-md p-2 flex gap-2">
                <AlertCircle size={14} className="text-gray-600 mt-0.5 flex-shrink-0" />
                <span className="text-xs text-gray-700">
                  Fri modus registrerer rammen men håndhever ingenting. Tilsvarer å ikke sette ramme.
                </span>
              </div>
            )}
          </div>

          {/* assessor_type_required */}
          <div>
            <label className="label">Sensor-type *</label>
            <select
              className="input"
              value={framework.assessor_type_required}
              onChange={(e) => update({ assessor_type_required: e.target.value as AssessmentFramework['assessor_type_required'] })}
              disabled={disabled}
            >
              {Object.entries(ASSESSOR_LABELS).map(([key, label]) => (
                <option key={key} value={key}>{label}</option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-1">
              Krever ekstern sensor at eksamen sensureres av uavhengig person. Intern vurderer tillater kursleder/lærer.
            </p>
          </div>

          {/* controlled_conditions_required + rubric_required */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <label className="flex items-start gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={framework.controlled_conditions_required}
                onChange={(e) => update({ controlled_conditions_required: e.target.checked })}
                disabled={disabled}
                className="mt-1"
              />
              <div>
                <div className="text-sm font-medium">Kontrollerte forhold</div>
                <div className="text-xs text-gray-500">
                  SEB, fysisk oppmøte eller tilsvarende tilsyn.
                </div>
              </div>
            </label>

            <label className="flex items-start gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={framework.rubric_required}
                onChange={(e) => update({ rubric_required: e.target.checked })}
                disabled={disabled}
                className="mt-1"
              />
              <div>
                <div className="text-sm font-medium">Vurderingsmatrise (rubric) kreves</div>
                <div className="text-xs text-gray-500">
                  Strukturert matrise med kriterier og nivåer. Anbefalt for praktisk/muntlig/observasjon.
                </div>
              </div>
            </label>
          </div>

          {/* bloom_min_level */}
          <div>
            <label className="label">Minimum Bloom-nivå (1-6)</label>
            <input
              type="number"
              className="input"
              min={1}
              max={6}
              value={framework.bloom_min_level}
              onChange={(e) => update({ bloom_min_level: Math.min(6, Math.max(1, parseInt(e.target.value) || 3)) })}
              disabled={disabled}
            />
            <p className="text-xs text-gray-500 mt-1">
              1=Huske · 2=Forstå · 3=Anvende · 4=Analysere · 5=Vurdere · 6=Skape
            </p>
          </div>

          {/* notes */}
          <div>
            <label className="label">Begrunnelse / notat (valgfritt)</label>
            <textarea
              className="input"
              rows={2}
              value={framework.notes || ''}
              onChange={(e) => update({ notes: e.target.value })}
              disabled={disabled}
              placeholder="F.eks. 'Fag-/svenneprøveforskrift krever ekstern sensor'"
            />
          </div>
        </div>
      )}
    </div>
  );
}
